import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useStorage } from '@vueuse/core'
import * as Sentry from "@sentry/vue"
import { showSuccess } from '@/services/notification'

import { 
  login as loginApi,
  getProfile,
  refreshToken as refreshTokenApi,
  killSessions,
  updateProfile as updateProfileApi,
  deleteUser,
} from '@/api/auth'

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()

  const localTokens = useStorage('auth_tokens', {})
  const pendingInvitationCode = useStorage('pending_invitation_code', null)
  const user = ref(null)
  const isAuthenticated = ref(false)

  const isSuperAdmin = computed(() => user.value?.isSuperAdmin || false)

  async function init() {
    try {
      let activeTokens = null
      if (localTokens.value.access_token && localTokens.value.refresh_token) {
        activeTokens = localTokens.value
      }
      if (activeTokens && activeTokens.access_token) {
        const now = Date.now()
        if (activeTokens.access_token_expires_in && now >= activeTokens.access_token_expires_in) {
          if (activeTokens.refresh_token_expires_in && now < activeTokens.refresh_token_expires_in) {
            const refreshed = await refreshAccessToken()
            if (!refreshed) {
              logout()
              return
            }
          } else {
            logout()
            return
          }
        }

        isAuthenticated.value = true
        await updateProfile()
      } else {
        clearTokens()
        isAuthenticated.value = false
      }
    } catch (error) {
      console.error('Auth initialization error:', error)
    }
  }

  function clearTokens() {
    localTokens.value = null
    user.value = null
    localStorage.removeItem('originalToken')
    Sentry.setUser(null)
  }

  async function login({ email, password }) {
    try {
      const response = await loginApi(email, password)

      if (response.access_token) {
        const now = Date.now()
        const tokenExp = now + Number(response.access_token_expires_in) * 1000
        const refreshTokenExp = now + Number(response.refresh_token_expires_in) * 1000
        const tokenData = {
          access_token: response.access_token,
          refresh_token: response.refresh_token,
          access_token_expires_in: tokenExp,
          refresh_token_expires_in: refreshTokenExp,
        }
        user.value = response.user 
        localTokens.value = tokenData
        isAuthenticated.value = true
        Sentry.setUser({
          username: response.user.firstName + ' ' + response.user.lastName,
          email: response.user.email,
        })
        
        return response
      } else {
        throw new Error('No token received')
      }
    } catch (error) {
      console.error('Login error:', error)
      throw error
    }
  }

  async function refreshAccessToken() {
    try {
      if (!localTokens.value.refresh_token) {
        return false
      }
      const response = await refreshTokenApi(localTokens.value.refresh_token)
      if (response && response.access_token) {
        const now = Date.now()
      
        const tokenExp = now + Number(response.access_token_expires_in) * 1000
        let refresh_token_expires_in = localTokens.value.refresh_token_expires_in

        const refresh_token = response.refresh_token || localTokens.value.refresh_token

        if (response.refresh_token_expires_in) {
          refresh_token_expires_in = now + Number(response.refresh_token_expires_in) * 1000
        }

        localTokens.value = {
          access_token: response.access_token,
          refresh_token,
          access_token_expires_in: tokenExp,
          refresh_token_expires_in: refresh_token_expires_in,
        }

        return true
      }

      return false
    } catch (error) {
      console.error('Token refresh error:', error)
      return false
    }
  }

  async function logout(kill = false) {
    try {
      if(kill){
        await killSessions()
      }
      clearTokens()
      isAuthenticated.value = false
      
      // Notification Store zurücksetzen beim Logout
      const { useNotificationStore } = await import('@/stores/notifications')
      const notificationStore = useNotificationStore()
      notificationStore.reset()
      
      router.replace({path: "/"})
      return true
    } catch (error) {
      console.error('Logout error:', error)
      throw error
    }
  }
    async function deleteMe() {
    try {
      await deleteUser()
      clearTokens()
      isAuthenticated.value = false
      router.replace({name: "login"})
      showSuccess('Der Benutzer wurde erfolgreich gelöscht.')
      return true
    } catch (error) {
      console.error('Delete error:', error)
      throw error
    }
  }
 async function updateProfile() {
    try {
      const data  = await getProfile()
      if (!data) {
        throw new Error('profileUpdateError')
      }
      user.value = data
      Sentry.setUser({
        username: data.firstName + ' ' + data.lastName,
        email: data.email,
      })
      
      return data
    } catch (error) {
      console.error('Error updating profile:', error)
      throw error
    }
  }

  async function updateToken() {
    const now = Date.now()
    try {
      const expiryBuffer = 120 * 1000
        if (localTokens.value.access_token_expires_in && (now + expiryBuffer) >= localTokens.value.access_token_expires_in) {
          if (localTokens.value.refresh_token_expires_in && (now + expiryBuffer) < localTokens.value.refresh_token_expires_in) {
          const refreshed = await refreshAccessToken()
          if (!refreshed) {
            console.warn('Token konnte nicht erneuert werden, Benutzer wird abgemeldet')
            await logout()
            return false
          }
          await updateProfile()
          return true
        } else {
          console.warn('Refresh-Token ist abgelaufen, Benutzer wird abgemeldet')
          await logout()
          return false
        }
      }
      return true
    } catch (error) {
      console.error('Fehler bei der Token-Aktualisierung:', error)
      await logout()
      return false
    }
  }




  function setPendingInvitationCode(code) {
    pendingInvitationCode.value = code
  }

  function getPendingInvitationCode() {
    return pendingInvitationCode.value
  }

  function clearPendingInvitationCode() {
    pendingInvitationCode.value = null
  }

  
  function setImpersonatedTokens(tokens, impersonatedUser) {
    const now = Date.now()
    const tokenExp = now + Number(tokens.access_token_expires_in) * 1000
    const refreshTokenExp = now + Number(tokens.refresh_token_expires_in) * 1000
    const originalTokens = {
      access_token: localTokens.value.access_token,
      refresh_token: localTokens.value.refresh_token,
      access_token_expires_in: localTokens.value.access_token_expires_in,
      refresh_token_expires_in: localTokens.value.refresh_token_expires_in,
    }
    localStorage.setItem('originalToken', JSON.stringify(originalTokens))
    
    const tokenData = {
      access_token: tokens.access_token,
      refresh_token: tokens.refresh_token,
      access_token_expires_in: tokenExp,
      refresh_token_expires_in: refreshTokenExp,
      isImpersonated: true,
      originalUser: user.value
    }
    user.value = impersonatedUser
    localTokens.value = tokenData
    isAuthenticated.value = true
    Sentry.setUser({
      username: impersonatedUser.firstName + ' ' + impersonatedUser.lastName,
      email: impersonatedUser.email,
    })
  }

  function isImpersonated() {
    return localTokens.value?.isImpersonated || false
  }

  function getOriginalUser() {
    return localTokens.value?.originalUser || null
  }

  function stopImpersonation() {
    if (isImpersonated()) {
      const originalUser = getOriginalUser()
      if (originalUser) {
        const originalTokensJson = localStorage.getItem('originalToken')
        if (originalTokensJson) {
          try {
            const originalTokens = JSON.parse(originalTokensJson)
            localTokens.value = {
              ...originalTokens,
              isImpersonated: false,
              originalUser: null
            }
            localStorage.removeItem('originalToken')
          } catch (error) {
            console.error('Error restoring original tokens:', error)
            localTokens.value = {
              ...localTokens.value,
              isImpersonated: false,
              originalUser: null
            }
          }
        } else {
          localTokens.value = {
            ...localTokens.value,
            isImpersonated: false,
            originalUser: null
          }
        }
        
        user.value = originalUser
        isAuthenticated.value = true
        Sentry.setUser({
          username: originalUser.firstName + ' ' + originalUser.lastName,
          email: originalUser.email,
        })
      }
    }
  }

  return {
    user,
    isAuthenticated,
    localTokens,
    pendingInvitationCode,
    deleteMe,
    init,
    login,
    logout,
    updateToken,
    updateProfile,
    refreshAccessToken,
    setPendingInvitationCode,
    getPendingInvitationCode,
    clearPendingInvitationCode,
    isSuperAdmin,
    setImpersonatedTokens,
    isImpersonated,
    getOriginalUser,
    stopImpersonation,
  }
})
